import React, {
    useState, useRef, useEffect, useCallback, useMemo, useImperativeHandle, forwardRef
} from 'react';
import { wrapFunctionalComponent } from 'utils/framework';

/**
 * PinchZoomPan - A React functional component for interactive zoom and pan
 *
 * Features:
 * - Pinch to zoom (two-finger touch gesture)
 * - Double-tap to zoom (quick double-tap toggles zoom)
 * - Pan/drag (both touch and mouse)
 * - No external dependencies
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to zoom/pan
 * @param {number} [props.maxScale=5] - Maximum zoom level
 * @param {number} [props.maxScale=2] - Maximum zoom level
 * @param {number} [props.initialScale=1] - Starting zoom level
 * @param {boolean} [props.enableDoubleTap=true] - Enable double-tap zoom
 * @param {boolean} [props.enablePinch=true] - Enable pinch zoom
 * @param {boolean} [props.enablePan=true] - Enable panning
 * @param {string|number} [props.width='100%'] - Container width
 * @param {string|number} [props.height='100%'] - Container height
 * @param {function} [props.onTransformChange] - Callback(scale, position)
 */
const PinchZoomPan = forwardRef(
    (
        {
            children,
            minScale = 0.5,
            maxScale = 2,
            initialScale = 1,
            enableDoubleTap = true,
            enablePinch = true,
            enablePan = true,
            width = '100%',
            height = '100%',
            onTransformChange
        },
        ref
    ) => {
        // State for rendering
        const [scale, setScale] = useState(() => Math.min(Math.max(initialScale, minScale), maxScale));
        const [position, setPosition] = useState({ x: 0, y: 0 });
        const [initialSize, setInitialSize] = useState(-1);
        const [effectiveMin, setEffectiveMin] = useState(-1);
        const [effectiveMax, setEffectiveMax] = useState(-1);

        // Effective min/max scale bounds derived from pixel size when initialSize is known.
        // effectiveMin = minScale * initialScale means the image cannot shrink below minScale
        // times its initial rendered size in pixels (e.g. minScale=0.5, initialSize.width=430px
        // → image never smaller than 215px).
        const effectiveMinScale = useMemo(() => {
            if (initialSize !== -1 && initialSize !== null && initialSize.width > 0) {
                return minScale * initialScale;
            }

            return minScale;
        }, [initialSize, minScale, initialScale]);

        const effectiveMaxScale = useMemo(() => {
            if (initialSize !== -1 && initialSize !== null && initialSize.width > 0) {
                return maxScale * initialScale;
            }

            return maxScale;
        }, [initialSize, maxScale, initialScale]);

        // Refs for DOM elements
        const containerRef = useRef(null);
        const contentRef = useRef(null);

        // Refs for gesture tracking (non-rendering state)
        const gestureState = useRef({
            // Pinch tracking
            initialDistance: 0,
            initialScale: 1,
            pinchCenter: { x: 0, y: 0 },
            isPinching: false,

            // Pan tracking
            isPanning: false,
            panStartPosition: { x: 0, y: 0 },
            transformStartPosition: { x: 0, y: 0 },

            // Double-tap tracking
            lastTapTime: 0,
            lastTapPosition: { x: 0, y: 0 },

            // Animation tracking
            animationFrameId: null,

            // Mouse tracking
            isMouseDown: false
        });

        // Styles
        const containerStyle = {
            width: typeof width === 'number' ? `${width}px` : width,
            height: typeof height === 'number' ? `${height}px` : height,
            overflow: 'hidden',
            position: 'relative',
            touchAction: 'none',
            userSelect: 'none',
            cursor: gestureState.current.isMouseDown ? 'grabbing' : 'grab'
        };

        const contentStyle = {
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transformOrigin: '50% 50%',
            willChange: 'transform',
            transition: gestureState.current.animationFrameId ? 'none' : 'transform 0.1s ease-out',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        };

        // Helper function: Calculate distance between two touch points
        const calculateDistance = useCallback((touch1, touch2) => {
            const dx = touch2.clientX - touch1.clientX;
            const dy = touch2.clientY - touch1.clientY;

            return Math.sqrt(dx * dx + dy * dy);
        }, []);

        // Helper function: Calculate midpoint between two touches
        const calculateMidpoint = useCallback((touch1, touch2) => {
            return {
                x: (touch1.clientX + touch2.clientX) / 2,
                y: (touch1.clientY + touch2.clientY) / 2
            };
        }, []);

        // Helper function: Constrain scale within bounds
        const constrainScale = useCallback(
            value => {
                return Math.min(Math.max(value, effectiveMinScale), effectiveMaxScale);
            },
            [effectiveMinScale, effectiveMaxScale]
        );

        useEffect(() => {
            if (initialScale && initialSize === -1) {
                const img = containerRef.current?.querySelector('img');
                const computedSize = img ? img.getBoundingClientRect() : null;

                if (!computedSize || computedSize.width === 0) {
                    return;
                }

                const min = computedSize && computedSize.width > 0 ? minScale * initialScale : minScale;
                const max = computedSize && computedSize.width > 0 ? maxScale * initialScale : maxScale;

                setInitialSize(computedSize);
                setEffectiveMin(min);
                setEffectiveMax(max);
                setScale(s => Math.min(Math.max(s, min), max));
            }
        }, [initialScale, initialSize, minScale, maxScale]);

        // Helper function: Constrain position to prevent panning into empty space
        const constrainPosition = useCallback((pos, currentScale) => {
            if (!containerRef.current || !contentRef.current) {
                return pos;
            }

            // At scale 1 or less, keep content at top-left
            if (currentScale <= 1) {
                return { x: 0, y: 0 };
            }

            const container = containerRef.current.getBoundingClientRect();

            // With transformOrigin '50% 50%', the content scales from its center.
            // Panning range is symmetric: ±(containerSize * (scale - 1) / 2)
            const halfPanX = (container.width * (currentScale - 1)) / 2;
            const halfPanY = (container.height * (currentScale - 1)) / 2;
            const maxX = halfPanX;
            const maxY = halfPanY;
            const minX = -halfPanX;
            const minY = -halfPanY;

            return {
                x: Math.min(Math.max(pos.x, minX), maxX),
                y: Math.min(Math.max(pos.y, minY), maxY)
            };
        }, []);

        // Expose imperative handle for parent components
        useImperativeHandle(
            ref,
            () => ({
                updatePosition: newPosition => {
                    const constrainedPosition = constrainPosition(newPosition, scale);
                    setPosition(constrainedPosition);
                }
            }),
            [scale, constrainPosition]
        );

        // Helper function: Get relative position within container
        const getRelativePosition = useCallback((clientX, clientY) => {
            if (!containerRef.current) {
                return { x: 0, y: 0 };
            }

            const rect = containerRef.current.getBoundingClientRect();

            return {
                x: clientX - rect.left,
                y: clientY - rect.top
            };
        }, []);

        // Helper function: Animate zoom transition
        const animateZoom = useCallback(
            (targetScale, targetPosition, duration = 200) => {
                const startScale = scale;
                const startPosition = { ...position };
                const startTime = performance.now();
                const constrainedTargetScale = constrainScale(targetScale);
                const constrainedTargetPosition = constrainPosition(targetPosition, constrainedTargetScale);

                const animate = currentTime => {
                    const elapsed = currentTime - startTime;
                    const progress = Math.min(elapsed / duration, 1);

                    // Ease-out function
                    const eased = 1 - Math.pow(1 - progress, 3);

                    const newScale = constrainScale(startScale + (constrainedTargetScale - startScale) * eased);
                    const newPosition = {
                        x: startPosition.x + (constrainedTargetPosition.x - startPosition.x) * eased,
                        y: startPosition.y + (constrainedTargetPosition.y - startPosition.y) * eased
                    };

                    const img = containerRef.current?.querySelector('img');
                    const computedSize = img?.getBoundingClientRect();

                    if (computedSize && computedSize.width > 0 && effectiveMin !== -1 && effectiveMax !== -1) {
                        if (computedSize.width < effectiveMin) {
                            return;
                        }

                        if (computedSize.width > effectiveMax) {
                            return;
                        }
                    }

                    setScale(newScale);
                    setPosition(constrainPosition(newPosition, newScale));

                    if (progress < 1) {
                        gestureState.current.animationFrameId = requestAnimationFrame(animate);
                    }
                };

                if (gestureState.current.animationFrameId) {
                    cancelAnimationFrame(gestureState.current.animationFrameId);
                }

                gestureState.current.animationFrameId = requestAnimationFrame(animate);
            },
            [scale, position, constrainScale, constrainPosition]
        );

        // Touch Start Handler
        const handleTouchStart = useCallback(
            e => {
                const touches = e.touches;

                if (touches.length === 2 && enablePinch) {
                    // Two touches: Initialize pinch
                    e.preventDefault();

                    const distance = calculateDistance(touches[0], touches[1]);
                    const midpoint = calculateMidpoint(touches[0], touches[1]);
                    const relativeCenter = getRelativePosition(midpoint.x, midpoint.y);

                    gestureState.current.isPinching = true;
                    gestureState.current.initialDistance = distance;
                    gestureState.current.initialScale = scale;
                    gestureState.current.pinchCenter = relativeCenter;
                    gestureState.current.transformStartPosition = { ...position };
                } else if (touches.length === 1 && enablePan) {
                    // One touch: Initialize pan
                    const touch = touches[0];

                    gestureState.current.isPanning = true;
                    gestureState.current.panStartPosition = {
                        x: touch.clientX,
                        y: touch.clientY
                    };
                    gestureState.current.transformStartPosition = { ...position };
                }
            },
            [enablePinch, enablePan, scale, position, calculateDistance, calculateMidpoint, getRelativePosition]
        );

        // Touch Move Handler
        const handleTouchMove = useCallback(
            e => {
                const touches = e.touches;

                if (touches.length === 2 && gestureState.current.isPinching && enablePinch) {
                    // Pinch zoom
                    e.preventDefault();

                    const currentDistance = calculateDistance(touches[0], touches[1]);
                    const distanceRatio = currentDistance / gestureState.current.initialDistance;
                    const newScale = constrainScale(gestureState.current.initialScale * distanceRatio);

                    // Keep position fixed at top-left during zoom
                    const newPosition = gestureState.current.transformStartPosition;
                    const constrainedPosition = constrainPosition(newPosition, newScale);

                    setScale(newScale);
                    setPosition(constrainedPosition);
                } else if (touches.length === 1 && gestureState.current.isPanning && enablePan && !gestureState.current.isPinching) {
                    // Pan
                    e.preventDefault();

                    const touch = touches[0];
                    const deltaX = touch.clientX - gestureState.current.panStartPosition.x;
                    const deltaY = touch.clientY - gestureState.current.panStartPosition.y;

                    const newPosition = {
                        x: gestureState.current.transformStartPosition.x + deltaX,
                        y: gestureState.current.transformStartPosition.y + deltaY
                    };

                    const constrainedPosition = constrainPosition(newPosition, scale);
                    setPosition(constrainedPosition);
                }
            },
            [enablePinch, enablePan, scale, calculateDistance, constrainScale, constrainPosition]
        );

        // Touch End Handler
        const handleTouchEnd = useCallback(
            e => {
                const touches = e.changedTouches;

                // Check for double-tap
                if (touches.length === 1 && enableDoubleTap && !gestureState.current.isPinching) {
                    const touch = touches[0];
                    const currentTime = Date.now();
                    const timeSinceLastTap = currentTime - gestureState.current.lastTapTime;

                    const lastPos = gestureState.current.lastTapPosition;
                    const distance = Math.sqrt(Math.pow(touch.clientX - lastPos.x, 2) + Math.pow(touch.clientY - lastPos.y, 2));

                    const DOUBLE_TAP_DELAY = 300;
                    const DOUBLE_TAP_DISTANCE = 20;

                    if (timeSinceLastTap < DOUBLE_TAP_DELAY && distance < DOUBLE_TAP_DISTANCE) {
                        // Double-tap detected
                        e.preventDefault();

                        if (scale <= effectiveMinScale) {
                            // Zoom in to 2x with top-left fixed
                            const targetScale = constrainScale(2);
                            const targetPosition = { x: 0, y: 0 };

                            animateZoom(targetScale, constrainPosition(targetPosition, targetScale));
                        } else {
                            // Zoom out to min scale, top-left fixed
                            animateZoom(effectiveMinScale, { x: 0, y: 0 });
                        }

                        gestureState.current.lastTapTime = 0; // Reset to prevent triple-tap
                    } else {
                        // Single tap
                        gestureState.current.lastTapTime = currentTime;
                        gestureState.current.lastTapPosition = {
                            x: touch.clientX,
                            y: touch.clientY
                        };
                    }
                }

                // Reset gesture state
                if (e.touches.length === 0) {
                    gestureState.current.isPinching = false;
                    gestureState.current.isPanning = false;
                }
            },
            [enableDoubleTap, scale, effectiveMinScale, animateZoom, constrainPosition, constrainScale]
        );

        // Mouse Down Handler
        const handleMouseDown = useCallback(
            e => {
                if (!enablePan) {
                    return;
                }

                e.preventDefault();
                gestureState.current.isMouseDown = true;
                gestureState.current.panStartPosition = {
                    x: e.clientX,
                    y: e.clientY
                };
                gestureState.current.transformStartPosition = { ...position };
            },
            [enablePan, position]
        );

        // Mouse Move Handler
        const handleMouseMove = useCallback(
            e => {
                if (!gestureState.current.isMouseDown || !enablePan) {
                    return;
                }

                e.preventDefault();

                const deltaX = e.clientX - gestureState.current.panStartPosition.x;
                const deltaY = e.clientY - gestureState.current.panStartPosition.y;

                const newPosition = {
                    x: gestureState.current.transformStartPosition.x + deltaX,
                    y: gestureState.current.transformStartPosition.y + deltaY
                };

                const constrainedPosition = constrainPosition(newPosition, scale);
                setPosition(constrainedPosition);
            },
            [enablePan, scale, constrainPosition]
        );

        // Mouse Up Handler
        const handleMouseUp = useCallback(() => {
            gestureState.current.isMouseDown = false;
        }, []);

        // Mouse Leave Handler
        const handleMouseLeave = useCallback(() => {
            gestureState.current.isMouseDown = false;
        }, []);

        // Wheel Handler (optional desktop zoom)
        const handleWheel = useCallback(
            e => {
                if (!enablePinch) {
                    return;
                }

                e.preventDefault();

                const delta = -e.deltaY;
                const scaleFactor = delta > 0 ? 1.1 : 0.9;
                const newScale = constrainScale(scale * scaleFactor);

                // Keep position fixed at top-left during zoom
                const constrainedPosition = constrainPosition(position, newScale);

                setScale(newScale);
                setPosition(constrainedPosition);
            },
            [enablePinch, scale, position, constrainScale, constrainPosition]
        );

        // Set up event listeners
        useEffect(() => {
            const container = containerRef.current;

            if (!container) {
                return undefined;
            }

            // Touch events
            container.addEventListener('touchstart', handleTouchStart, { passive: false });
            container.addEventListener('touchmove', handleTouchMove, { passive: false });
            container.addEventListener('touchend', handleTouchEnd);

            // Mouse events
            container.addEventListener('mousedown', handleMouseDown);
            container.addEventListener('mousemove', handleMouseMove);
            container.addEventListener('mouseup', handleMouseUp);
            container.addEventListener('mouseleave', handleMouseLeave);

            // Wheel event
            container.addEventListener('wheel', handleWheel, { passive: false });

            // Prevent context menu on long press
            container.addEventListener('contextmenu', e => e.preventDefault());

            return () => {
                container.removeEventListener('touchstart', handleTouchStart);
                container.removeEventListener('touchmove', handleTouchMove);
                container.removeEventListener('touchend', handleTouchEnd);
                container.removeEventListener('mousedown', handleMouseDown);
                container.removeEventListener('mousemove', handleMouseMove);
                container.removeEventListener('mouseup', handleMouseUp);
                container.removeEventListener('mouseleave', handleMouseLeave);
                container.removeEventListener('wheel', handleWheel);

                if (gestureState.current.animationFrameId) {
                    cancelAnimationFrame(gestureState.current.animationFrameId);
                }
            };
        }, [handleTouchStart, handleTouchMove, handleTouchEnd, handleMouseDown, handleMouseMove, handleMouseUp, handleMouseLeave, handleWheel]);

        // Callback when transform changes
        useEffect(() => {
            if (onTransformChange) {
                onTransformChange(scale, position);
            }
        }, [scale, position, onTransformChange]);

        return (
            <div
                ref={containerRef}
                style={containerStyle}
            >
                <div
                    ref={contentRef}
                    style={contentStyle}
                >
                    {children}
                </div>
            </div>
        );
    }
);

export default wrapFunctionalComponent(PinchZoomPan, 'PinchZoomPan');
